import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, X } from 'lucide-react';
import { palette } from '../utils/designTokens';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { enhanceTracesResponse, isInDemoMode } from '../services/demoDataService';
import { normalizeV1Trace, normalizeV2Trace } from '../services/tracesApi';
import { v2ServicePath } from '../config';
import ChatTurn from './ChatTurn';
import CopyButton from './CopyButton';

const DEFAULT_WIDTH = 475;
const MIN_WIDTH = 200;
const MAX_WIDTH = 600;

/**
 * SessionChatStrip - Resizable, collapsible panel showing session conversation context
 * on the Trace Detail page. Highlights the current trace and allows
 * navigating to sibling traces in the same session.
 */
export default function SessionChatStrip({ sessionId, currentTraceId, appId, sessionJwt, tenantId, onTraceChange }) {
  const { selectedEnvironment } = useEnvironment();
  const { selectedRange } = useTimeRange();
  const isDemo = isInDemoMode(tenantId, selectedEnvironment, appId);
  const sessionIdLabel = sessionId && sessionId.length > 18 ? `${sessionId.slice(0, 18)}...` : sessionId;
  const sessionLink = useMemo(() => {
    if (!appId || !sessionId) return '';
    const params = new URLSearchParams();
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    if (selectedRange) params.set('range', selectedRange);
    const query = params.toString();
    return `/app/${appId}/sessions/${encodeURIComponent(sessionId)}${query ? `?${query}` : ''}`;
  }, [appId, selectedEnvironment, selectedRange, sessionId]);

  const [traces, setTraces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const currentTurnRef = useRef(null);

  const sortedTraces = useMemo(() => {
    if (!traces) return [];
    return [...traces].sort((a, b) => {
      const aTime = a.trace_start ? new Date(a.trace_start).getTime() : 0;
      const bTime = b.trace_start ? new Date(b.trace_start).getTime() : 0;
      return aTime - bTime;
    });
  }, [traces]);

  // Fetch session traces
  useEffect(() => {
    if (!sessionId || !sessionJwt) return;
    const abortController = new AbortController();

    const fetchTraces = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const params = new URLSearchParams();
        params.append('session_id', sessionId);
        params.append('limit', '200');

        params.append('env', selectedEnvironment || '');
        params.append('range', selectedRange || '');

        const endpoint = `${apiUrl}${v2ServicePath(appId, '/traces')}?${params.toString()}`;

        const response = await fetch(endpoint, {
          signal: abortController.signal,
          headers: {
            Authorization: `Bearer ${sessionJwt}`,
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok && !isDemo) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const rawData = response.ok ? await response.json() : {};
        const data = isDemo
          ? enhanceTracesResponse(rawData, tenantId, selectedEnvironment, appId)
          : rawData;

        const rows = isDemo
          ? (Array.isArray(data.traces) ? data.traces.map(normalizeV1Trace) : [])
          : (Array.isArray(data.data) ? data.data.map(normalizeV2Trace) : []);
        setTraces(rows);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('SessionChatStrip: Failed to fetch session traces:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTraces();
    return () => abortController.abort();
  }, [sessionId, sessionJwt, selectedEnvironment, selectedRange, appId, isDemo, tenantId]);

  // Auto-scroll to current trace (including when expanding from collapsed state)
  useEffect(() => {
    if (collapsed) return;
    if (!currentTurnRef.current) return;

    // Wait for panel mount/layout before scrolling when opening.
    const id = window.requestAnimationFrame(() => {
      currentTurnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    return () => window.cancelAnimationFrame(id);
  }, [collapsed, sortedTraces, currentTraceId]);

  const handleSelect = useCallback((traceId) => {
    if (traceId === currentTraceId) return;
    const trace = sortedTraces.find(t => t.trace_id === traceId);
    if (onTraceChange) {
      onTraceChange(traceId, trace);
    }
  }, [currentTraceId, sortedTraces, onTraceChange]);

  // Resize drag handlers
  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    const container = document.querySelector('.trace-detail-layout');
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const newWidth = e.clientX - containerRect.left;
    setWidth(Math.min(Math.max(newWidth, MIN_WIDTH), MAX_WIDTH));
  }, []);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  const handleMouseDown = useCallback(() => {
    isDraggingRef.current = true;
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Don't render if no session
  if (!sessionId) return null;
  if (!loading && sortedTraces.length === 0) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="flex flex-col items-center justify-center px-1.5 shrink-0 rounded-l-md border border-r-0 border-accent-primary-base bg-accent-primary-base/15 hover:bg-accent-primary-base/25 transition-colors"
        title={`Show session conversation: ${sessionId}`}
      >
        <MessageSquare size={14} style={{ color: palette.accents.primary.base }} />
        <span
          className="mt-1.5 text-[10px] font-semibold"
          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', color: palette.accents.primary.base }}
        >
          Session ({sortedTraces.length})
        </span>
      </button>
    );
  }

  return (
    <div className="flex shrink-0">
      {/* Panel content */}
      <div
        className="flex flex-col border-r border-border-subtle bg-background-secondary overflow-hidden"
        style={{ width: `${width}px` }}
      >
        <div className="flex items-start justify-between gap-2 px-2 py-2 border-b border-border-subtle">
          <div className="flex min-w-0 items-start gap-1.5">
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              className="p-0.5 rounded hover:bg-background-tertiary"
              title="Collapse session panel"
            >
              <MessageSquare size={12} style={{ color: palette.accents.primary.base }} />
            </button>
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="shrink-0 text-xs font-semibold text-text-primary">Session</span>
              <span className="shrink-0 text-[10px] text-text-muted">({sortedTraces.length})</span>
              {sessionLink ? (
                <Link
                  to={sessionLink}
                  className="min-w-0 truncate font-mono text-[10px] text-accent-primary-base hover:underline"
                  title={sessionId}
                >
                  {sessionIdLabel}
                </Link>
              ) : (
                <span
                  className="min-w-0 truncate font-mono text-[10px] text-text-muted"
                  title={sessionId}
                >
                  {sessionIdLabel}
                </span>
              )}
              <CopyButton text={sessionId} size="xs" label="Copy Session ID" />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="p-1 rounded hover:bg-background-tertiary"
            title="Close session panel"
          >
            <X size={14} style={{ color: palette.text.muted }} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-1.5 space-y-1.5">
          {loading && (
            <div className="text-[10px] text-text-muted p-2">Loading session...</div>
          )}
          {sortedTraces.map((trace) => {
            const isCurrent = trace.trace_id === currentTraceId;
            return (
              <div key={trace.trace_id} ref={isCurrent ? currentTurnRef : undefined}>
                <ChatTurn
                  trace={trace}
                  isSelected={isCurrent}
                  onSelect={handleSelect}
                  compact
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Resize handle */}
      <div
        className="w-1 hover:w-2 transition-all cursor-col-resize flex items-center justify-center group"
        style={{ backgroundColor: isDragging ? palette.accents.primary.base : palette.borders.subtle }}
        onMouseDown={handleMouseDown}
      >
        <div
          className="w-0.5 h-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ backgroundColor: palette.accents.primary.base }}
        />
      </div>
    </div>
  );
}
